import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Amber banner shown above the customer dashboard when their driver licence is expiring
 * within 60 days (or already expired). Reads `user.license_expiry` (ISO YYYY-MM-DD) and
 * does no API calls. Renders nothing when:
 *   - the customer hasn't set an expiry,
 *   - the expiry is >60 days out.
 *
 * The expired state uses a rose tint (more urgent) and a tweaked copy. Bookings will be
 * blocked server-side when `license_expiry < end_date` of the requested rental (iter 41
 * guard), so this is the customer's heads-up.
 */
export default function LicenseExpiryBanner({ licenseExpiry }) {
  const { t } = useTranslation();
  if (!licenseExpiry) return null;
  // Parse as YYYY-MM-DD components to avoid TZ drift (testing-agent feedback).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(licenseExpiry);
  if (!m) return null;
  const exp = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.round((exp.getTime() - today.getTime()) / msPerDay);
  if (days > 60) return null;

  const expired = days < 0;
  const tone = expired
    ? "border-rose-200 bg-rose-50 text-rose-900"
    : "border-amber-200 bg-amber-50 text-amber-900";
  const iconTone = expired ? "text-rose-600" : "text-amber-600";

  let title;
  if (expired) title = t("dashboard.licenseExpired");
  else if (days === 0) title = t("dashboard.licenseExpiresToday");
  else title = t("dashboard.licenseExpiresIn", { count: days });

  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border ${tone} px-4 py-3 mb-6`}
      data-testid="license-expiry-banner"
      role="alert"
    >
      <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${iconTone}`} />
      <div className="text-sm leading-relaxed">
        <div className="font-semibold mb-0.5">{title}</div>
        <div className="text-xs opacity-90">
          {expired
            ? t("dashboard.licenseExpiredHelp", { date: licenseExpiry })
            : t("dashboard.licenseExpiringHelp", { date: licenseExpiry })}
        </div>
      </div>
    </div>
  );
}
